import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Eye, EyeOff, Shield, User, Warehouse } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Button from '../../components/ui/Button';
import Input from '../../components/forms/Input';
import Modal from '../../components/ui/Modal';
import UserForm from './UserForm';
import UserDetails from './UserDetails';
import apiService from '../../services/apiService';
import './UsersPage.css';

const UsersPage = () => {
    const { loading, setLoading, setError, clearError } = useApp();

    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showPasswords, setShowPasswords] = useState({});

    // Cargar usuarios al montar el componente
    useEffect(() => {
        loadUsers();
    }, []);

    // Filtrar usuarios cuando cambie la búsqueda
    useEffect(() => {
        filterUsers();
    }, [users, searchQuery]);

    const loadUsers = async () => {
        try {
            setLoading(true);
            clearError();

            const result = await apiService.getUsuarios();

            if (result.success) {
                setUsers(result.data);
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al cargar usuarios');
        } finally {
            setLoading(false);
        }
    };

    const filterUsers = () => {
        if (!searchQuery.trim()) {
            setFilteredUsers(users);
            return;
        }

        const query = searchQuery.toLowerCase();
        const filtered = users.filter(user =>
            user.nombre.toLowerCase().includes(query) ||
            user.usuario.toLowerCase().includes(query) ||
            user.email.toLowerCase().includes(query) ||
            user.rol_nombre?.toLowerCase().includes(query)
        );

        setFilteredUsers(filtered);
    };

    const handleCreateUser = async (userData) => {
        try {
            const result = await apiService.crearUsuario(userData);

            if (result.success) {
                setShowCreateModal(false);
                loadUsers(); // Recargar lista
                // Mostrar mensaje de éxito (opcional)
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al crear usuario');
        }
    };

    const handleEditUser = async (userData) => {
        try {
            const result = await apiService.actualizarUsuario({
                user_id: selectedUser.id,
                ...userData
            });

            if (result.success) {
                setShowEditModal(false);
                setSelectedUser(null);
                loadUsers(); // Recargar lista
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al actualizar usuario');
        }
    };

    const handleDeleteUser = async (userId, userName) => {
        if (!window.confirm(`¿Estás seguro de que quieres eliminar al usuario "${userName}"?`)) {
            return;
        }

        try {
            const result = await apiService.eliminarUsuario({ user_id: userId });

            if (result.success) {
                loadUsers(); // Recargar lista
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al eliminar usuario');
        }
    };

    const togglePasswordVisibility = (userId) => {
        setShowPasswords(prev => ({
            ...prev,
            [userId]: !prev[userId]
        }));
    };

    const getRoleIcon = (roleName) => {
        switch (roleName?.toLowerCase()) {
            case 'administrador':
                return <Shield size={16} className="role-icon admin" />;
            case 'gerente':
                return <User size={16} className="role-icon manager" />;
            case 'cajero':
                return <User size={16} className="role-icon cashier" />;
            case 'almacenista':
                return <User size={16} className="role-icon warehouse" />;
            default:
                return <User size={16} className="role-icon default" />;
        }
    };

    const getRoleClass = (roleName) => {
        const normalizedName = roleName?.toLowerCase().replace(/\s+/g, '-');
        switch (normalizedName) {
            case 'administrador':
                return 'role-badge admin';
            case 'gerente':
                return 'role-badge manager';
            case 'cajero':
                return 'role-badge cashier';
            case 'almacenista':
                return 'role-badge warehouse';
            default:
                return 'role-badge default';
        }
    };

    return (
        <div className="users-page">
            {/* Header */}
            <div className="users-header">
                <div className="header-content">
                    <h1 className="page-title">
                        <User size={24} />
                        Gestión de Usuarios
                    </h1>
                    <p className="page-description">
                        Administra los usuarios del sistema, sus roles y permisos
                    </p>
                </div>

                <div className="header-actions">
                    <Button
                        onClick={() => setShowCreateModal(true)}
                        className="create-user-btn"
                        disabled={loading}
                    >

                        Nuevo Usuario
                    </Button>
                </div>
            </div>

            {/* Filtros y búsqueda */}
            <div className="users-filters">
                <div className="search-container">
                    <Input
                        placeholder="Buscar por nombre, usuario, email o rol..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        icon={<Search size={20} />}
                        className="search-input"
                    />
                </div>

                <div className="filters-summary">
                    <span className="users-count">
                        {filteredUsers.length} de {users.length} usuarios
                    </span>
                </div>
            </div>

            {/* Tabla de usuarios */}
            <div className="users-table-container">
                {loading && (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Cargando usuarios...</p>
                    </div>
                )}

                {!loading && filteredUsers.length === 0 && (
                    <div className="empty-state">
                        <User size={48} />
                        <h3>No se encontraron usuarios</h3>
                        <p>
                            {searchQuery
                                ? 'No hay usuarios que coincidan con tu búsqueda'
                                : 'No hay usuarios registrados en el sistema'
                            }
                        </p>
                        {!searchQuery && (
                            <Button
                                onClick={() => setShowCreateModal(true)}
                                className="empty-action-btn"
                            >

                                Crear primer usuario
                            </Button>
                        )}
                    </div>
                )}

                {!loading && filteredUsers.length > 0 && (
                    <table className="users-table-compact">
                        <thead>
                            <tr>
                                <th width="20%">Usuario</th>
                                <th width="20%">Contacto</th>
                                <th width="12%">Rol</th>
                                <th width="15%">Almacenes</th>
                                <th width="8%">Estado</th>
                                <th width="12%">Último Acceso</th>
                                <th width="13%">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map((user) => (
                                <tr key={user.id} className={user.activo ? '' : 'inactive-row'}>
                                    <td>
                                        <div className="user-compact">
                                            <div className="user-avatar-small">
                                                {user.nombre.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="user-info-compact">
                                                <div className="user-name-compact">{user.nombre}</div>
                                                <div className="username-compact">@{user.usuario}</div>
                                            </div>
                                        </div>
                                    </td>

                                    <td>
                                        <div className="contact-compact">
                                            <div className="email-compact">{user.email}</div>
                                            {user.telefono && (
                                                <div className="phone-compact">{user.telefono}</div>
                                            )}
                                        </div>
                                    </td>

                                    <td>
                                        <div className="role-display-compact">
                                            <span className={getRoleClass(user.rol_nombre)}>
                                                {user.rol_nombre || 'Sin rol'}
                                            </span>
                                        </div>
                                    </td>

                                    <td>
                                        <div className="almacenes-display-compact">
                                            {user.almacenes && user.almacenes.length > 0 ? (
                                                <div className="almacenes-badges">
                                                    {user.almacenes.slice(0, 2).map((almacen, index) => (
                                                        <span key={index} className="almacen-badge" title={almacen.nombre}>

                                                            <span className="almacen-nombre">{almacen.codigo || almacen.nombre}</span>
                                                        </span>
                                                    ))}
                                                    {user.almacenes.length > 2 && (
                                                        <span className="almacen-more" title={`${user.almacenes.length - 2} más`}>
                                                            +{user.almacenes.length - 2}
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="no-almacenes">-</span>
                                            )}
                                        </div>
                                    </td>

                                    <td>
                                        <span className={`status-dot ${user.activo ? 'active' : 'inactive'}`}>
                                            {user.activo ? '● Activo' : '● Inactivo'}
                                        </span>
                                    </td>

                                    <td>
                                        <span className="date-compact">
                                            {user.ultimo_acceso
                                                ? new Date(user.ultimo_acceso).toLocaleDateString('es-ES', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: '2-digit'
                                                })
                                                : 'Nunca'
                                            }
                                        </span>
                                    </td>

                                    <td>
                                        <div className="actions-compact">
                                            <button
                                                className="action-btn-compact"
                                                onClick={() => {
                                                    setSelectedUser(user);
                                                    setShowDetailsModal(true);
                                                }}
                                                title="Ver detalles"
                                            >
                                                <Eye size={14} />
                                            </button>

                                            <button
                                                className="action-btn-compact"
                                                onClick={() => {
                                                    setSelectedUser(user);
                                                    setShowEditModal(true);
                                                }}
                                                title="Editar"
                                            >
                                                <Edit size={14} />
                                            </button>

                                            <button
                                                className="action-btn-compact delete"
                                                onClick={() => handleDeleteUser(user.id, user.nombre)}
                                                title="Eliminar"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modales */}
            {showCreateModal && (
                <Modal
                    title="Crear Nuevo Usuario"
                    onClose={() => setShowCreateModal(false)}
                    size="large"
                >
                    <UserForm
                        onSubmit={handleCreateUser}
                        onCancel={() => setShowCreateModal(false)}
                        isLoading={loading}
                    />
                </Modal>
            )}

            {showEditModal && selectedUser && (
                <Modal
                    title="Editar Usuario"
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedUser(null);
                    }}
                    size="large"
                >
                    <UserForm
                        user={selectedUser}
                        onSubmit={handleEditUser}
                        onCancel={() => {
                            setShowEditModal(false);
                            setSelectedUser(null);
                        }}
                        isLoading={loading}
                        isEdit={true}
                    />
                </Modal>
            )}

            {showDetailsModal && selectedUser && (
                <Modal
                    title="Detalles del Usuario"
                    onClose={() => {
                        setShowDetailsModal(false);
                        setSelectedUser(null);
                    }}
                    size="large"
                >
                    <UserDetails
                        user={selectedUser}
                        onClose={() => {
                            setShowDetailsModal(false);
                            setSelectedUser(null);
                        }}
                        onEdit={() => {
                            setShowDetailsModal(false);
                            setShowEditModal(true);
                        }}
                    />
                </Modal>
            )}
        </div>
    );
};

export default UsersPage;